import React, { useState } from "react";
import {
  Table, Tag, Button, Popconfirm, Space, Tooltip, message, Badge,
} from "antd";
import {
  UndoOutlined, StopOutlined, DeleteOutlined, EyeOutlined,
} from "@ant-design/icons";
import { apiRequests } from "src/shared/api/api";
import TechOperationDetailModal from "src/features/Modal/ui/TechOperationDetailModal";

const STATUS_CONFIG = {
  active:   { color: "green",   text: "Активна"       },
  reversed: { color: "orange",  text: "Распроведена"  },
  canceled: { color: "default", text: "Отменена"      },
  deleted:  { color: "red",     text: "Удалена"       },
};

export default function TableTechOperations({ operations, loading, token, onRefresh }) {
  const [detailRecord, setDetailRecord] = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  const setRowLoading = (id, val) =>
    setActionLoading(prev => ({ ...prev, [id]: val }));

  const handleReverse = async (record) => {
    setRowLoading(record.id, "reverse");
    try {
      await apiRequests.tech_operations.reverse(record.id);
      message.success("Тех. операция распроведена, складские документы откатаны");
      if (onRefresh) await onRefresh();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Ошибка при распроведении";
      message.error(detail);
    } finally {
      setRowLoading(record.id, null);
    }
  };

  const handleCancel = async (record) => {
    setRowLoading(record.id, "cancel");
    try {
      await apiRequests.tech_operations.cancel(record.id);
      message.success("Тех. операция отменена");
      if (onRefresh) await onRefresh();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Ошибка при отмене";
      message.error(detail);
    } finally {
      setRowLoading(record.id, null);
    }
  };

  const handleDelete = async (id) => {
    setRowLoading(id, "delete");
    try {
      await apiRequests.tech_operations.delete(id);
      message.success("Тех. операция удалена");
      if (onRefresh) await onRefresh();
    } catch (err) {
      message.error("Ошибка при удалении");
    } finally {
      setRowLoading(id, null);
    }
  };

  const columns = [
    {
      title: "ID / Дата",
      dataIndex: "id",
      key: "id",
      width: 220,
      render: (id, record) => (
        <div>
          <div style={{ fontSize: 11, color: "#888", fontFamily: "monospace" }}>
            {String(id).slice(0, 8)}…
          </div>
          <div style={{ fontSize: 12 }}>
            {record.created_at
              ? new Date(record.created_at).toLocaleString("ru-RU")
              : "—"}
          </div>
        </div>
      ),
    },
    {
      title:     "Статус",
      dataIndex: "status",
      key:       "status",
      width:     130,
      filters:   Object.entries(STATUS_CONFIG).map(([v, c]) => ({ text: c.text, value: v })),
      onFilter:  (val, row) => row.status === val,
      render:    (status) => {
        const cfg = STATUS_CONFIG[status] || { color: "default", text: status };
        return <Tag color={cfg.color}>{cfg.text}</Tag>;
      },
    },
    {
      title:     "Тех. карта",
      dataIndex: "tech_card_id",
      key:       "tech_card_id",
      ellipsis:  true,
      render:    (id) => (
        <span style={{ fontFamily: "monospace", fontSize: 12 }}>
          {id ? String(id).slice(0, 8) + "…" : "—"}
        </span>
      ),
    },
    {
      title:     "Склад (сырьё)",
      dataIndex: "from_warehouse_id",
      key:       "from_warehouse_id",
      width:     120,
      render:    (id) => id ? <Tag>#{id}</Tag> : "—",
    },
    {
      title:     "Склад (продукт)",
      dataIndex: "to_warehouse_id",
      key:       "to_warehouse_id",
      width:     130,
      render:    (id) => id ? <Tag color="blue">#{id}</Tag> : "—",
    },
    {
      title:     "Кол-во (выход)",
      dataIndex: "output_quantity",
      key:       "output_quantity",
      width:     120,
      render:    (v) => v != null ? <Badge count={v} color="#52c41a" overflowCount={999999} /> : "—",
    },
    {
      title:     "Документ списания",
      dataIndex: "consumption_doc_id",
      key:       "consumption_doc_id",
      width:     150,
      render:    (id) => id
        ? <Tag color="red">Списание #{id}</Tag>
        : <Tag color="default">—</Tag>,
    },
    {
      title:     "Документ производства",
      dataIndex: "production_doc_id",
      key:       "production_doc_id",
      width:     160,
      render:    (id) => id
        ? <Tag color="green">Производство #{id}</Tag>
        : <Tag color="default">—</Tag>,
    },
    {
      title:     "Продажа",
      dataIndex: "docs_sales_id",
      key:       "docs_sales_id",
      width:     100,
      render:    (id) => id ? <Tag>#{id}</Tag> : "—",
    },
    {
      title:  "Действия",
      key:    "action",
      fixed:  "right",
      width:  180,
      render: (_, record) => {
        const rowLoading = actionLoading[record.id];
        const isActive   = record.status === "active";

        return (
          <Space size={4}>
            {/* Просмотр деталей */}
            <Tooltip title="Детали операции">
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => setDetailRecord(record)}
              />
            </Tooltip>

            {/* Распровести — только для active */}
            {isActive && (
              <Tooltip title="Распровести (откатить складские документы)">
                <Popconfirm
                  title="Распровести тех. операцию?"
                  description={
                    <>
                      Будут созданы обратные складские движения.<br />
                      Документы списания и производства будут аннулированы.
                    </>
                  }
                  onConfirm={() => handleReverse(record)}
                  okText="Распровести"
                  cancelText="Отмена"
                  okButtonProps={{ danger: true }}
                >
                  <Button
                    size="small"
                    icon={<UndoOutlined />}
                    loading={rowLoading === "reverse"}
                    danger
                  />
                </Popconfirm>
              </Tooltip>
            )}

            {/* Отменить — только для active */}
            {isActive && (
              <Tooltip title="Отменить (без отката складских документов)">
                <Popconfirm
                  title="Отменить тех. операцию?"
                  description="Складские документы НЕ будут откатаны."
                  onConfirm={() => handleCancel(record)}
                  okText="Отменить"
                  cancelText="Нет"
                >
                  <Button
                    size="small"
                    icon={<StopOutlined />}
                    loading={rowLoading === "cancel"}
                  />
                </Popconfirm>
              </Tooltip>
            )}

            {/* Удалить */}
            <Tooltip title="Удалить">
              <Popconfirm
                title="Удалить тех. операцию?"
                onConfirm={() => handleDelete(record.id)}
                okText="Да"
                cancelText="Нет"
                okButtonProps={{ danger: true }}
              >
                <Button
                  size="small"
                  icon={<DeleteOutlined />}
                  loading={rowLoading === "delete"}
                  danger
                />
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Table
        rowKey="id"
        dataSource={operations}
        columns={columns}
        loading={loading}
        scroll={{ x: 1400 }}
        size="small"
        bordered
        rowClassName={(record) => {
          if (record.status === "reversed") return "tech-op-row-reversed";
          if (record.status === "canceled") return "tech-op-row-canceled";
          return "";
        }}
        pagination={{
          pageSize: 30,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}–${range[1]} из ${total} операций`,
        }}
      />

      {/* Модальное окно деталей */}
      <TechOperationDetailModal
        record={detailRecord}
        onClose={() => setDetailRecord(null)}
      />
    </>
  );
}